import React, { useState, useEffect } from 'react';

// Hooks
import { useNavigate } from 'react-router-dom';

// Utils
import { MESSAGES } from '~/utils/message';
import { dateFormat } from '~/utils/date-format';
import { getEvent } from '~/utils/helperFunctions';
import { DEFAULT_PAGE_SIZES } from '~/utils/constants';

// MUI Components
import DataGrid from '~/components/General/OlcDataGrid';
import { Box, Typography } from '@mui/material';

// Components
import OlcTablePagination from '~/components/General/OlcTablePagination';
import TruncatedText from '~/components/General/TruncatedText';

// Styles
import './styles.scss';

const WebhookLogTable = (props) => {
  const { rows, setSelectedWebhookLog, pagination, setPagination, loading, webhookId } = props;
  const [showNoRows, setShowNoRows] = useState(false);

  const navigate = useNavigate();

  const handleRowClick = (params) => {
    setSelectedWebhookLog(params.row);
    navigate(`/settings/webhooks/${webhookId}/webhook-log/${params.row.id}`, {
      state: { webhookLog: params.row },
    });
  };

  const getEventWithTestFlag = (row) => {
    const eventName = getEvent(row);
    // Check if request body contains isTest: true
    const isTest = row?.value?.request?.body?.isTest || false;
    return isTest ? `${eventName} [test]` : eventName;
  };

  const columns = [
    {
      field: 'event',
      headerName: 'Event',
      sortable: false,
      valueGetter: (params) => getEventWithTestFlag(params.row),
      renderCell: (params) => (
        <Box className="tagsWrapper">
          <TruncatedText text={getEventWithTestFlag(params.row)} />
        </Box>
      ),
    },
    {
      field: 'responseStatusCode',
      headerName: 'Status Code',
      sortable: false,
      valueGetter: (params) =>
        params.row?.responseStatusCode || params.row?.value?.response?.status || ' -',
      renderCell: (params) => (
        <Box className="tagsWrapper">
          <TruncatedText
            text={params.row?.responseStatusCode || params.row?.value?.response?.status || ' -'}
          />
        </Box>
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Date',
      sortable: false,
      valueGetter: (params) => dateFormat(params.row?.createdAt) || ' -',
      renderCell: (params) => (
        <Box className="tagsWrapper">
          <TruncatedText text={dateFormat(params.row?.createdAt) || ' -'} />
        </Box>
      ),
    },
  ];

  const handleChangePage = (event, page) => {
    setPagination({
      page: page,
      pageSize: pagination.pageSize,
    });
  };

  const handlePageSizeChange = (event) => {
    setPagination({
      page: 1,
      pageSize: event.target.value,
    });
  };

  useEffect(() => {
    const currentPage = rows?.currentPage || pagination?.page || 1;

    if (rows?.rows?.length === 0 && currentPage > 1) {
      handleChangePage('', currentPage - 1);
    }
  }, [rows?.rows]);

  useEffect(() => {
    let timeoutId;

    if (!loading && rows?.rows?.length === 0) {
      timeoutId = setTimeout(() => setShowNoRows(true), 300);
    } else {
      setShowNoRows(false);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [loading, rows?.rows?.length]);

  return (
    <div className="webhookLog">
      <Typography className="title">{MESSAGES.SETTINGS.WEBHOOKS.LOGS.TITLE}</Typography>

      <Box
        className={`olcTableGridInternal ${loading ? 'table-loading' : ''} ${rows?.rows?.length === 0 ? 'table-empty' : ''}`}
      >
        <Box sx={{ position: 'relative' }}>
          <DataGrid
            rows={rows?.rows || []}
            rowCount={rows?.total || 0}
            columns={columns}
            paginationMode="server"
            hideFooterSelectedRowCount
            loading={loading}
            hideFooterPagination
            rowSelection={false}
            onRowClick={handleRowClick}
            getRowId={(row) => row?.id}
            disableColumnMenu
            disableRowSelectionOnClick
            rowHeight={61}
            sx={
              showNoRows
                ? {
                    height: 'auto',
                    minHeight: '200px',
                  }
                : undefined
            }
            slots={{
              noRowsOverlay: () =>
                showNoRows ? (
                  <Typography className="webhookLogNoResults">No Results Found</Typography>
                ) : null,
              noResultsOverlay: () =>
                showNoRows ? (
                  <Typography className="webhookLogNoResults">No Results Found</Typography>
                ) : null,
            }}
          />
        </Box>

        {(loading || rows?.total > 0) && (
          <OlcTablePagination
            count={rows?.lastPage || 1}
            page={rows?.currentPage || pagination?.page || 1}
            pageSize={pagination?.pageSize}
            pageSizes={DEFAULT_PAGE_SIZES}
            onPageChange={handleChangePage}
            onPageSizeChange={handlePageSizeChange}
            from={rows?.from}
            to={rows?.to}
            total={rows?.total || 0}
            loading={loading}
          />
        )}
      </Box>
    </div>
  );
};

export default WebhookLogTable;
